import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Avatar,
  Typography,
  TextField,
  Button,
  Stack,
  useTheme,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import SaveIcon from "@mui/icons-material/Save";
import API from "../utils/api";
import { toast } from "react-toastify";
import useAuth from "../hooks/useAuth";
import DisabledButtonWrapper from "../components/DisabledButtonWrapper";
import GreenGlowOutlineButton from "../components/GreenGlowOutlineButton";
import { ImageUp } from "lucide-react";
import StateSelect from "../components/StateSelect";
import { formatPhone } from "../utils/formatPhone";

export default function CompanyProfilePage() {
  const theme = useTheme();
  const { isReadOnly } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [companyData, setCompanyData] = useState({
    name: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "",
    phone: "",
    email: "",
    website: "",
    taxId: "",
    logoUrl: "",
  });
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("");
  const [removeLogo, setRemoveLogo] = useState(false);
  const MAX_LOGO_MB = 1;
  const MAX_LOGO_BYTES = MAX_LOGO_MB * 1024 * 1024;
  const displayLogoSrc = removeLogo
    ? undefined
    : (logoPreviewUrl || companyData.logoUrl || undefined);
  const dangerButtonSx = {
    borderRadius: "14px",
    fontWeight: 700,
    fontSize: "0.875rem",
    letterSpacing: "-0.01em",
    lineHeight: 1.4,
    padding: "8px 16px",
    whiteSpace: "nowrap",
    transition: "all 0.18s ease",
    borderColor: "error.main",
    color: "error.main",
    "&:hover": {
      borderColor: "error.dark",
      color: "error.dark",
      backgroundColor: (t) =>
        t.palette.mode === "dark" ? "rgba(239,68,68,0.16)" : "rgba(239,68,68,0.08)",
      transform: "translateY(-1px)",
    },
    "&:active": {
      transform: "translateY(0)",
    },
  };

  useEffect(() => {
    fetchCompanyProfile();
  }, []);

  useEffect(() => {
    return () => {
      if (logoPreviewUrl) {
        URL.revokeObjectURL(logoPreviewUrl);
      }
    };
  }, [logoPreviewUrl]);

  const fetchCompanyProfile = async () => {
    try {
      setLoading(true);
      const res = await API.get("/company-profile/");
      const data = res.data;
      setCompanyData({
        name: data.name || "",
        address: data.address || "",
        city: data.city || "",
        state: data.state || "",
        zipCode: data.zip_code || "",
        country: data.country || "",
        phone: formatPhone(data.phone),
        email: data.email || "",
        website: data.website || "",
        taxId: data.tax_id || "",
        logoUrl: data.logo_url || "",
      });
      setSelectedLogoFile(null);
      setLogoPreviewUrl("");
      setRemoveLogo(false);
    } catch (err) {
      console.error("Failed to load company profile", err);
      toast.error("Failed to load company profile");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setCompanyData((prev) => ({ ...prev, [field]: value }));
  };

  const handleLogoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_LOGO_BYTES) {
      toast.error(`Logo must be ${MAX_LOGO_MB} MB or smaller.`);
      e.target.value = "";
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file.");
      e.target.value = "";
      return;
    }

    setSelectedLogoFile(file);
    setRemoveLogo(false);
    const preview = URL.createObjectURL(file);
    setLogoPreviewUrl(preview);
  };

  const handleRemoveLogo = () => {
    setSelectedLogoFile(null);
    setLogoPreviewUrl("");
    setRemoveLogo(true);
  };

  const handleSave = async () => {
    if (isReadOnly) return; // Kill the click for read-only users
    
    try {
      setSaving(true);
      const payload = new FormData();
      payload.append("name", companyData.name);
      payload.append("address", companyData.address);
      payload.append("city", companyData.city);
      payload.append("state", companyData.state);
      payload.append("zip_code", companyData.zipCode);
      payload.append("country", companyData.country);
      payload.append("phone", formatPhone(companyData.phone));
      payload.append("email", companyData.email);
      payload.append("website", companyData.website);
      payload.append("tax_id", companyData.taxId);
      payload.append("remove_logo", removeLogo ? "true" : "false");
      if (selectedLogoFile) {
        payload.append("logo", selectedLogoFile);
      }
      await API.put("/company-profile/", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Company profile saved successfully");
      await fetchCompanyProfile();
      window.dispatchEvent(new CustomEvent("companyProfileUpdated"));
    } catch (err) {
      console.error("Failed to save company profile", err);
      toast.error("Failed to save company profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3, px: { xs: 1, sm: 3 } }}>
      <Box sx={{ maxWidth: 1440, mx: "auto", width: "100%" }}>
        {/* Header Card */}
        <Card sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(25,118,210,0.08)",
                }}
              >
                <BusinessIcon color="primary" />
              </Box>

              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Company Profile
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage your company information and details
                </Typography>
              </Box>
            </Stack>

            <DisabledButtonWrapper 
              isDisabled={isReadOnly || saving}
              tooltipText={saving ? "Saving in progress..." : "Read-only mode: This action is disabled"}
            >
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={isReadOnly || saving}
                sx={{
                  borderRadius: "14px",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  letterSpacing: "-0.01em",
                  lineHeight: 1.4,
                  padding: "8px 16px",
                  whiteSpace: "nowrap",
                  transition: "all 0.18s ease",
                  background: (theme) =>
                    `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  color: "#fff",
                  boxShadow: (theme) => `0 10px 22px ${theme.palette.primary.main}47`,
                  opacity: (isReadOnly || saving) ? 0.6 : 1,
                  cursor: (isReadOnly || saving) ? "not-allowed" : "pointer",
                  visibility: "visible",
                  display: "inline-flex",
                  "&:hover": {
                    background: (theme) =>
                      `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.dark} 100%)`,
                    boxShadow: (theme) => `0 14px 28px ${theme.palette.primary.main}61`,
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                    boxShadow: (theme) => `0 6px 14px ${theme.palette.primary.main}40`,
                  },
                }}
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </DisabledButtonWrapper>
          </Stack>
        </Card>

        {/* Main Form Card */}
        <Card sx={{ borderRadius: 3 }} data-tour="company-profile-form">
          <CardContent>
            <Typography variant="h6" fontWeight={700} mb={3}>
              Company Information
            </Typography>

            <Box sx={{ mb: 3 }} data-tour="company-profile-logo">
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                White Label Logo
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                <Avatar
                  src={displayLogoSrc}
                  alt={companyData.name || "Company logo"}
                  variant="rounded"
                  sx={{
                    width: 220,
                    height: 56,
                    bgcolor: "transparent",
                    border: "1px solid",
                    borderColor: "divider",
                    "& img": {
                      objectFit: "contain",
                      objectPosition: "left center",
                      p: 0.75,
                    },
                  }}
                />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <DisabledButtonWrapper
                    isDisabled={isReadOnly || saving}
                    tooltipText={saving ? "Saving in progress..." : "Read-only mode: This action is disabled"}
                  >
                    <GreenGlowOutlineButton
                      component="label"
                      disabled={isReadOnly || saving}
                      startIcon={<ImageUp size={16} strokeWidth={2.25} />}
                    >
                      Upload Logo
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleLogoFileChange}
                      />
                    </GreenGlowOutlineButton>
                  </DisabledButtonWrapper>
                  <DisabledButtonWrapper
                    isDisabled={isReadOnly || saving || (!companyData.logoUrl && !logoPreviewUrl)}
                    tooltipText={
                      saving
                        ? "Saving in progress..."
                        : isReadOnly
                        ? "Read-only mode: This action is disabled"
                        : "No logo to remove"
                    }
                  >
                    <Button
                      variant="outlined"
                      disabled={isReadOnly || saving || (!companyData.logoUrl && !logoPreviewUrl)}
                      onClick={handleRemoveLogo}
                      sx={dangerButtonSx}
                    >
                      Remove Logo
                    </Button>
                  </DisabledButtonWrapper>
                </Stack>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Max file size: {MAX_LOGO_MB} MB. Recommended transparent PNG or SVG with wide aspect ratio.
              </Typography>
            </Box>

            <Box
              display="grid"
              gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }}
              gap={3}
            >
              <TextField
                label="Company Name"
                value={companyData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                fullWidth
                required
              />

              <TextField
                label="Email"
                type="email"
                value={companyData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                fullWidth
              />

              <TextField
                label="Phone"
                type="tel"
                placeholder="(xxx) xxx-xxxx"
                value={formatPhone(companyData.phone)}
                onChange={(e) => handleChange("phone", formatPhone(e.target.value))}
                fullWidth
              />

              <TextField
                label="Website"
                type="url"
                value={companyData.website}
                onChange={(e) => handleChange("website", e.target.value)}
                fullWidth
              />

              <TextField
                label="Address"
                value={companyData.address}
                onChange={(e) => handleChange("address", e.target.value)}
                fullWidth
                multiline
                rows={2}
              />

              <TextField
                label="City"
                value={companyData.city}
                onChange={(e) => handleChange("city", e.target.value)}
                fullWidth
              />

              <StateSelect
                label="State"
                value={companyData.state}
                onChange={(e) => handleChange("state", e.target.value)}
              />

              <TextField
                label="ZIP/Postal Code"
                value={companyData.zipCode}
                onChange={(e) => handleChange("zipCode", e.target.value)}
                fullWidth
              />

              
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

